/**
 * ROSEYS Dramatic Loading Orchestrator
 *
 * Creates a curated, intentional digital experience:
 * - Everything black (#141414) until loaded
 * - Sequential top-to-bottom reveal
 * - Text never appears before imagery
 * - Hero forces attention first
 * - Graceful handling of slow connections
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    heroLoadTimeout: 5000,      // Max wait for hero (5s)
    sectionLoadTimeout: 3000,   // Max wait per section (3s)
    revealDelay: 150,           // Delay between section reveals
    scrollThreshold: 0.15,      // % of section visible to trigger
    loadingIndicatorDelay: 1500 // Show loading indicator after 1.5s
  };

  // State
  const state = {
    heroReady: false,
    sectionsLoaded: new Set(),
    sectionsInView: new Set(),
    revealQueue: [],
    isProcessingQueue: false
  };

  /**
   * Initialize the loading orchestrator
   */
  function init() {
    // Add loading-active class to body
    document.body.classList.add('loading-active');

    // Get all main content sections
    const sections = getSections();

    // Initialize each section
    sections.forEach((section, index) => {
      section.classList.add('section-loading');
      section.dataset.loadingIndex = index;

      // Show loading indicator after delay if still loading
      setTimeout(() => {
        if (section.classList.contains('section-loading')) {
          section.classList.add('show-loading-indicator');
        }
      }, CONFIG.loadingIndicatorDelay);
    });

    // Start hero loading
    loadHero(sections[0]);

    // Set up intersection observer for scroll-triggered reveals
    setupIntersectionObserver(sections);

    // Defer below-fold videos before setting up autoplay (prevents 15+ concurrent loads)
    deferBelowFoldVideos();

    // Set up video autoplay handlers
    setupVideoAutoplay();

    // Fallback: ensure content visible after timeout
    setTimeout(forceRevealAll, CONFIG.heroLoadTimeout + 2000);
  }

  /**
   * Get all main content sections
   */
  function getSections() {
    const mainContent = document.querySelector('.content-for-layout');
    if (!mainContent) return [];

    return Array.from(mainContent.querySelectorAll(':scope > .shopify-section'));
  }

  /**
   * Load hero section first
   */
  function loadHero(heroSection) {
    if (!heroSection) {
      heroReady();
      return;
    }

    const heroMedia = getMediaElements(heroSection);

    if (heroMedia.length === 0) {
      // No media, reveal immediately
      revealSection(heroSection);
      heroReady();
      return;
    }

    // Wait for hero media to load
    Promise.race([
      waitForMedia(heroMedia),
      timeout(CONFIG.heroLoadTimeout)
    ]).then(() => {
      revealSection(heroSection);
      heroReady();
    });
  }

  /**
   * Hero is ready - enable scrolling
   */
  function heroReady() {
    state.heroReady = true;
    document.body.classList.add('loading-hero-ready');
    document.body.classList.remove('loading-active');

    // Start processing queued sections
    processRevealQueue();
  }

  /**
   * Get media elements (images, videos) from section
   */
  function getMediaElements(section) {
    const images = Array.from(section.querySelectorAll('img[src], img[srcset]'));
    const videos = Array.from(section.querySelectorAll('video'));
    return [...images, ...videos];
  }

  /**
   * Wait for media elements to load
   */
  function waitForMedia(mediaElements) {
    const promises = mediaElements.map(media => {
      if (media.tagName === 'IMG') {
        return waitForImage(media);
      } else if (media.tagName === 'VIDEO') {
        return waitForVideo(media);
      }
      return Promise.resolve();
    });

    return Promise.all(promises);
  }

  /**
   * Wait for image to load
   */
  function waitForImage(img) {
    return new Promise(resolve => {
      if (img.complete && img.naturalHeight > 0) {
        resolve();
        return;
      }

      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  /**
   * Wait for video to be ready to play
   */
  function waitForVideo(video) {
    return new Promise(resolve => {
      // Video is ready if it has enough data
      if (video.readyState >= 3) {
        resolve();
        return;
      }

      // Wait for canplay event
      video.addEventListener('canplay', resolve, { once: true });
      video.addEventListener('loadeddata', resolve, { once: true });
      video.addEventListener('error', resolve, { once: true });

      // If video has poster, consider it ready faster
      if (video.poster) {
        const posterImg = new Image();
        posterImg.onload = resolve;
        posterImg.src = video.poster;
      }
    });
  }

  /**
   * Create timeout promise
   */
  function timeout(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Set up intersection observer for scroll-triggered reveals
   */
  function setupIntersectionObserver(sections) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const section = entry.target;
        const index = parseInt(section.dataset.loadingIndex, 10);

        if (entry.isIntersecting) {
          state.sectionsInView.add(index);
          section.classList.add('section-in-view');
          queueSectionReveal(section, index);
        }
      });
    }, {
      root: null,
      rootMargin: '50px 0px',
      threshold: CONFIG.scrollThreshold
    });

    // Skip hero (index 0), observe rest
    sections.slice(1).forEach(section => observer.observe(section));
  }

  /**
   * Queue section for reveal
   */
  function queueSectionReveal(section, index) {
    if (state.sectionsLoaded.has(index)) return;

    // Load section media first
    const media = getMediaElements(section);

    Promise.race([
      waitForMedia(media),
      timeout(CONFIG.sectionLoadTimeout)
    ]).then(() => {
      state.revealQueue.push({ section, index });

      // Sort queue by index to maintain order
      state.revealQueue.sort((a, b) => a.index - b.index);

      processRevealQueue();
    });
  }

  /**
   * Process reveal queue sequentially
   */
  function processRevealQueue() {
    if (!state.heroReady || state.isProcessingQueue) return;
    if (state.revealQueue.length === 0) return;

    state.isProcessingQueue = true;

    const next = state.revealQueue.shift();

    // Check if previous sections are loaded (maintain order)
    const previousLoaded = canRevealSection(next.index);

    if (previousLoaded) {
      revealSection(next.section);
      state.sectionsLoaded.add(next.index);

      // Continue processing after delay
      setTimeout(() => {
        state.isProcessingQueue = false;
        processRevealQueue();
      }, CONFIG.revealDelay);
    } else {
      // Put back in queue and wait
      state.revealQueue.unshift(next);
      state.isProcessingQueue = false;

      // Try again shortly
      setTimeout(processRevealQueue, 100);
    }
  }

  /**
   * Check if section can be revealed (previous sections loaded)
   */
  function canRevealSection(index) {
    // Hero (0) can always reveal
    if (index <= 1) return true;

    // Check if previous section is loaded or in view
    return state.sectionsLoaded.has(index - 1) ||
           state.sectionsInView.has(index - 1);
  }

  /**
   * Reveal a section
   */
  function revealSection(section) {
    section.classList.remove('section-loading', 'show-loading-indicator');
    section.classList.add('section-loaded');
  }

  /**
   * Defer below-fold videos: strip sources so they don't load until near viewport.
   * Must run BEFORE setupVideoAutoplay() to prevent immediate play attempts.
   */
  function deferBelowFoldVideos() {
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    document.querySelectorAll('video').forEach(function(video) {
      // Skip video journey videos — managed by hp-video-journey.liquid
      if (video.closest('#exp-master-container')) return;

      var rect = video.getBoundingClientRect();
      // If video top is within 1.5x viewport height, consider it "above fold" — let it load
      if (rect.top < viewportHeight * 1.5) return;

      // Below fold: strip sources to prevent loading
      video.querySelectorAll('source').forEach(function(source) {
        if (source.src && !source.dataset.lazySrc) {
          source.dataset.lazySrc = source.src;
          source.removeAttribute('src');
        }
      });
      // Also handle direct src on video element
      if (video.src && !video.dataset.lazySrc) {
        video.dataset.lazySrc = video.src;
        video.removeAttribute('src');
      }
      video.preload = 'none';
      video.dataset.lazyDeferred = 'true';
    });

    // Single IntersectionObserver to restore sources when near viewport
    var lazyObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        var video = entry.target.querySelector ? entry.target.querySelector('video[data-lazy-deferred]') : null;
        if (!video && entry.target.tagName === 'VIDEO') video = entry.target;
        if (!video || !video.dataset.lazyDeferred) return;

        // Restore sources
        video.querySelectorAll('source[data-lazy-src]').forEach(function(source) {
          source.src = source.dataset.lazySrc;
          delete source.dataset.lazySrc;
        });
        if (video.dataset.lazySrc) {
          video.src = video.dataset.lazySrc;
          delete video.dataset.lazySrc;
        }
        delete video.dataset.lazyDeferred;
        video.load();
        if (video.autoplay || video.hasAttribute('autoplay')) {
          video.play().catch(function() {});
        }
        lazyObserver.unobserve(entry.target);
      });
    }, { rootMargin: '200px 0px', threshold: 0.01 });

    // Observe the parent section of each deferred video (more reliable than observing video directly)
    document.querySelectorAll('video[data-lazy-deferred]').forEach(function(video) {
      var section = video.closest('.shopify-section') || video.parentElement;
      lazyObserver.observe(section);
    });
  }

  /**
   * Set up video autoplay handlers
   */
  function setupVideoAutoplay() {
    // Find all videos that should autoplay
    const videos = document.querySelectorAll('video[autoplay], .slide-video');

    videos.forEach(video => {
      // Skip video journey videos — they have their own playback management
      if (video.closest('#exp-master-container')) return;
      // Skip deferred below-fold videos — they'll be handled by lazy observer
      if (video.dataset.lazyDeferred) return;

      // Ensure video attributes
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      // Try to play when visible
      const playVideo = () => {
        if (video.paused) {
          video.play().catch(() => {
            // Autoplay blocked, that's ok
          });
        }
      };

      // Play when video can play
      video.addEventListener('canplay', playVideo);
      video.addEventListener('loadeddata', playVideo);

      // Play when section becomes visible
      const section = video.closest('.shopify-section');
      if (section) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              playVideo();
            }
          });
        }, { threshold: 0.1 });

        observer.observe(section);
      }

      // Try immediate play
      playVideo();
    });

    // Hide play buttons on autoplay containers
    document.querySelectorAll('.autoplay-true, .section-slideshow').forEach(container => {
      const playButtons = container.querySelectorAll('.video-play-button, [data-play-button]');
      playButtons.forEach(btn => {
        btn.style.display = 'none';
        btn.style.opacity = '0';
        btn.style.pointerEvents = 'none';
      });
    });
  }

  /**
   * Force reveal all sections (fallback)
   */
  function forceRevealAll() {
    document.body.classList.remove('loading-active');
    document.body.classList.add('loading-hero-ready', 'loading-complete');

    document.querySelectorAll('.section-loading').forEach(section => {
      revealSection(section);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also initialize after full load for any late content
  window.addEventListener('load', () => {
    // Give a moment for any async content
    setTimeout(processRevealQueue, 500);
  });

})();

// Respect prefers-reduced-motion: pause all autoplay videos
(function() {
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function handleMotionPreference(mq) {
    var videos = document.querySelectorAll('video[autoplay]');
    videos.forEach(function(video) {
      if (mq.matches) {
        video.pause();
        video.removeAttribute('autoplay');
        video.dataset.autoplayDisabled = 'reduced-motion';
      } else if (video.dataset.autoplayDisabled === 'reduced-motion') {
        video.setAttribute('autoplay', '');
        delete video.dataset.autoplayDisabled;
        video.play().catch(function() {});
      }
    });
  }

  if (prefersReducedMotion.addEventListener) {
    prefersReducedMotion.addEventListener('change', handleMotionPreference);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      handleMotionPreference(prefersReducedMotion);
    });
  } else {
    handleMotionPreference(prefersReducedMotion);
  }
})();
